import { IUserDashboardWidgetItemModel, UserDashboardWidgetItemModel, IUnitValue, IUnitDefinition } from 'shared/models';
import { formatValueWithUnits } from 'pages/profile-dashboard/utilities/formatValueWithUnits.ts';
import { getStatusColor } from 'pages/profile-dashboard/utilities/getStatusColor.ts';
import { useDashboardDict } from 'pages/profile-dashboard/context/useDashboardDict.tsx';
import { observer } from 'mobx-react';

import "./styles.scss";

type PPDWidgetBaseListProps = {
    items: UserDashboardWidgetItemModel[];
    onSelect: (row: IUserDashboardWidgetItemModel) => void;
    valueType?: "main" | "detailed";
    withDirection?: boolean
};
function pickValue(value?: IUnitValue | IUnitValue[] | null, type: "main" | "detailed" = "main") {
    if (!value) return null;
    if (Array.isArray(value)) {
        const found = value.find(v => v.type === type);
        return found ?? value[0] ?? null;
    }
    return value;
}

export const PPDWidgetStoresList = observer(({ items = [], onSelect, valueType = "main", withDirection = false }: PPDWidgetBaseListProps) => {
    const { stores, units } = useDashboardDict();

    if (!items.length) {
        return null;
    }

    const renderValueCell = (
        fact: IUnitValue | IUnitValue[] | null,
        withDirection = false
    ) => {
        const factVal = pickValue(fact, valueType);

        if (!factVal) return <span>—</span>;

        const isTrendUp = factVal.amount > 0;
        const isTrendDown = factVal.amount < 0;

        const color = withDirection
            ? isTrendUp
                ? "#22C55E"
                : isTrendDown
                    ? "#F43F5E"
                    : "#FB923C"
            : getStatusColor(factVal.amount, "#2c2f35");

        return (
            <span
                className="ppd-widget-stores-list__value"
                style={{ color }}
            >
                {formatValueWithUnits(factVal, units) ? formatValueWithUnits(factVal, units) : "-"}

                {withDirection && factVal.amount !== 0 && (
                    <svg
                        width="10"
                        height="11"
                        viewBox="0 0 10 11"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ transform: !isTrendUp ? 'rotate(180deg)' : '' }}
                    >
                        <path
                            d="M0.916687 5.50008L5.00002 1.41675M5.00002 1.41675L9.08335 5.50008M5.00002 1.41675V9.58341"
                            stroke="currentColor"
                            strokeWidth="1.16667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </span>
        );
    };

    return (
        <div className="ppd-widget-stores-list">
            {items.map((row, idx) => {
                const storeName =
                    (row.store_id != null
                        ? stores?.find((store) => store.id === row.store_id)?.name
                        : null) ?? "—";

                return (
                    <div
                        key={`${row.store_id ?? "id"}-${row.group_id ?? idx}`}
                        className="ppd-widget-stores-list__item"
                        onClick={() => onSelect(row)}
                    >
                        <div className="ppd-widget-stores-list__label">{storeName}</div>

                        {renderValueCell(row.value, withDirection)}
                    </div>
                );
            })}
        </div>
    );
});
